import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import {
	ArrowDownTrayIcon,
	EyeIcon,
	ShareIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useChatActions } from "../../contexts/chat-actions-context";
import { useAnalytics } from "../../lib/analytics";
import { formatFileSize, getFileExtension } from "../../lib/utils/file";
import { getDocumentViewUrl } from "../../services/documentService";
import type { Document } from "../../types/document";
import styles from "./FileCard.module.css";

interface FileCardProps {
	document: Document;
	t: (key: string, options?: Record<string, string>) => string;
	onShare: (document: Document) => void;
}

export function FileCard({ document: doc, t, onShare }: FileCardProps) {
	const { triggerMessage } = useChatActions();
	const { logEvent } = useAnalytics();
	const navigate = useNavigate();

	const fileExtension = getFileExtension(doc.mimeType, doc.name);
	const fileSize = formatFileSize(doc.fileSize);
	const uploadDate = format(new Date(doc.uploadedAt), "MMM d, yyyy");

	async function handleView() {
		logEvent("file_view_pressed", undefined, { documentId: doc.id });
		try {
			const { url } = await getDocumentViewUrl(doc.id);
			if (Capacitor.isNativePlatform()) {
				await Browser.open({ url });
			} else {
				globalThis.window.open(url, "_blank");
			}
		} catch {
			toast.error(t("actions.viewError"));
		}
	}

	function handleShare() {
		logEvent("file_share_pressed", undefined, { documentId: doc.id });
		onShare(doc);
	}

	async function handleDownload() {
		logEvent("file_download_pressed", undefined, { documentId: doc.id });
		try {
			const { url } = await getDocumentViewUrl(doc.id, true);
			if (Capacitor.isNativePlatform()) {
				await Browser.open({ url });
			} else {
				globalThis.window.open(url, "_blank");
			}
			toast.success(t("actions.downloadStarted"));
		} catch {
			toast.error(t("actions.viewError"));
		}
	}

	async function handleAskEve() {
		logEvent("file_ask_eve_pressed", undefined, { documentId: doc.id });
		try {
			const { url } = await getDocumentViewUrl(doc.id);
			triggerMessage(
				t("actions.askEveQuestion", { filename: doc.name }),
				undefined,
				[{ url, mediaType: doc.mimeType }],
			);
			navigate("/chat");
		} catch {
			toast.error(t("actions.viewError"));
		}
	}

	return (
		<div className={styles.card}>
			<div className={styles.infoArea}>
				<div className={styles.nameRow}>
					<span className={styles.fileName}>{doc.name}</span>
					<span className={styles.typeBadge}>{fileExtension}</span>
				</div>
				<span className={styles.details}>
					{fileSize} &middot; {uploadDate}
				</span>
			</div>
			<div className={styles.actionsRow}>
				<button
					type="button"
					className={styles.actionButton}
					onClick={handleView}
					aria-label={t("actions.viewFile")}
				>
					<EyeIcon className={styles.actionIcon} />
				</button>
				<button
					type="button"
					className={styles.actionButton}
					onClick={handleShare}
					aria-label={t("actions.shareFile")}
				>
					<ShareIcon className={styles.actionIcon} />
				</button>
				<button
					type="button"
					className={styles.actionButton}
					onClick={handleDownload}
					aria-label={t("actions.downloadFile")}
				>
					<ArrowDownTrayIcon className={styles.actionIcon} />
				</button>
				<button
					type="button"
					className={styles.askEveButton}
					onClick={handleAskEve}
				>
					<img
						src="/images/ask-eve-icon.png"
						alt=""
						className={styles.eveIcon}
					/>
					<span className={styles.askEveLabel}>{t("actions.askEve")}</span>
				</button>
			</div>
		</div>
	);
}
